import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from "react";
import queryString from "query-string";
import { Link } from "react-router-dom";
import httpService from "../../services/http.service";
import {
  formatCash,
  getUserFullName,
  numberWithSeparator,
  PERIODICITY_DAILY,
  PERIODICITY_MONTHLY,
  PERIODICITY_WEEKLY,
  PERIODICITY_YEARLY,
} from "../../utils/index";
import MyOpinionLogo from "../../assets/images/MyOpinion_Logo_Head.png";
import AppBreadcrumb from "../Shared/AppBreadcrumb";
import useAlert from "../../hooks/useAlert";
import AppAlert from "../Shared/AppAlert";
import { connect } from "react-redux";
import "./dashboard.css";
import { ShimmerBlock, ShimmerRows, ShimmerTableRows } from "../Shared/Shimmer";
import DashboardCharts from "./DashboardCharts";

const logDashboardApi = (path, payload) => {
  console.log(`[Dashboard API] ${path}`, payload);
};

/** Indicateur clé : pastille d'icône, libellé, valeur (ou shimmer pendant le chargement). */
const MetricTile = ({ icon, tone, label, value, loading, title }) => (
  <div className="metric-tile" title={title}>
    <span className={`metric-tile__icon metric-tile__icon--${tone}`} aria-hidden="true">
      <i className={`fa ${icon}`} />
    </span>
    <div className="metric-tile__text">
      <span className="metric-tile__label">{label}</span>
      {loading ? (
        <ShimmerBlock width={80} height={30} radius={8} />
      ) : (
        <strong className="metric-tile__value">{value}</strong>
      )}
    </div>
  </div>
);

const DashboardSectionLoader = ({ label }) => (
  <div className="body dashboard-stats-card__body">
    <ShimmerRows rows={4} label={label || i18n.t("Chargement…")} />
  </div>
);

const Dashboard = ({ authUser, history, location, match, ...props }) => {
  const { t } = useTranslation();
  const [isStatsMode, setIsStatsMode] = useState(false);

  useEffect(() => {
    setIsStatsMode(localStorage.getItem("isStatsMode") === "true");
  }, [location.hostname]);

  const statsMultiplier = isStatsMode ? 10 : 1;

  const handleToggleStatsMode = () => {
    setIsStatsMode(!isStatsMode);
    localStorage.setItem("isStatsMode", !isStatsMode);
  };

  const fetchActiveUsers = async (cb = () => null) => {
    try {
      const { data: response } = await httpService.get(`/stats/activeUsers`);
      logDashboardApi("GET /stats/activeUsers", response);
      cb(response);
      return response;
    } catch (e) {
      console.error("[Dashboard API] GET /stats/activeUsers", e?.response?.data ?? e);
      throw e;
    }
  };

  const fetchNewUsers = async (cb = () => null) => {
    try {
      const { data: response } = await httpService.get(`/stats/newUsers`);
      logDashboardApi("GET /stats/newUsers", response);
      cb(response);
      return response;
    } catch (e) {
      console.error("[Dashboard API] GET /stats/newUsers", e?.response?.data ?? e);
      throw e;
    }
  };

  const fetchTotalAuthorizationRequests = async (cb = () => null) => {
    try {
      const { data: response } = await httpService.get(
        `/stats/totalAuthorizationRequests`
      );
      logDashboardApi("GET /stats/totalAuthorizationRequests", response);
      cb(response);
      return response;
    } catch (e) {
      console.error(
        "[Dashboard API] GET /stats/totalAuthorizationRequests",
        e?.response?.data ?? e
      );
      throw e;
    }
  };

  const fetchTotalUsers = async (cb = () => null) => {
    try {
      const { data: response } = await httpService.get(`/stats/totalUsers`);
      logDashboardApi("GET /stats/totalUsers", response);
      cb(response);
      return response;
    } catch (e) {
      console.error("[Dashboard API] GET /stats/totalUsers", e?.response?.data ?? e);
      throw e;
    }
  };

  const fetchActiveOpinions = async (cb = () => null) => {
    try {
      const { data: response } = await httpService.get(`/stats/activeOpinions`);
      logDashboardApi("GET /stats/activeOpinions", response);
      cb(response);
      return response;
    } catch (e) {
      console.error(
        "[Dashboard API] GET /stats/activeOpinions",
        e?.response?.data ?? e
      );
      throw e;
    }
  };

  const fetchMostFollowedUsers = async (cb = () => null) => {
    try {
      const { data: response } = await httpService.get(`/stats/mostFollowedUser`);
      logDashboardApi("GET /stats/mostFollowedUser", response);
      cb(response);
      return response;
    } catch (e) {
      console.error(
        "[Dashboard API] GET /stats/mostFollowedUser",
        e?.response?.data ?? e
      );
      throw e;
    }
  };

  const fetchMonetizedAccounts = async (cb = () => null) => {
    try {
      const response = await httpService.get(`/payments/top-users-usd`);
      logDashboardApi("GET /payments/top-users-usd (axios response.data)", response?.data);
      const payload = response?.data?.data || response?.data || {};
      const usersWrapper = payload.users || {};
      const items = Array.isArray(usersWrapper)
        ? usersWrapper
        : usersWrapper.data || [];
      logDashboardApi("GET /payments/top-users-usd (items utilisés par l’UI)", items);
      cb(items);
      return items;
    } catch (error) {
      console.error(
        "[Dashboard API] GET /payments/top-users-usd",
        error?.response?.data ?? error
      );
      cb([]);
      return [];
    }
  };

  const navigateToUserProfile = (user) => {
    history.push(`/users/${user?.user_username}`);
  };

  const initialValues = {};

  const [loadingNewUsers, setLoadingNewUsers] = useState(true);
  const [loadingActiveUsers, setLoadingActiveUsers] = useState(true);
  const [loadingTotalUsers, setLoadingTotalUsers] = useState(true);
  const [loadingActiveOpinions, setLoadingActiveOpinions] = useState(true);
  const [loadingMostFollowed, setLoadingMostFollowed] = useState(true);
  const [activeUsers, setActiveUsers] = useState(null);
  const [newUsers, setNewUsers] = useState(null);
  const [activeOpinions, setActiveOpinions] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [mostFollowedUsers, setMostFollowedUsers] = useState([]);
  const [monetizedAccounts, setMonetizedAccounts] = useState([]);
  const [fetchingMonetizedAccounts, setFetchingMonetizedAccounts] =
    useState(true);
  const [authorisationRequestsAlert, setAuthorisationRequestsAlert, onClose] =
    useAlert();

  const getVariationClassName = (value) => {
    if (value === 0) return "";
    if (value > 0) return "text-success";
    if (value < 0) return "text-danger";
  };

  const getVariationCaret = (value) => {
    if (value === 0) return null;
    if (value > 0) return <i className="fa fa-caret-up"></i>;
    if (value < 0) return <i className="fa fa-caret-down"></i>;
  };

  useEffect(() => {
    fetchNewUsers()
      .then((data) => setNewUsers(data))
      .catch(() => setNewUsers(null))
      .finally(() => setLoadingNewUsers(false));

    fetchActiveUsers()
      .then((data) => setActiveUsers(data))
      .catch(() => setActiveUsers(null))
      .finally(() => setLoadingActiveUsers(false));

    fetchTotalUsers()
      .then((data) => setTotalUsers(data))
      .catch(() => setTotalUsers(0))
      .finally(() => setLoadingTotalUsers(false));

    fetchActiveOpinions()
      .then((data) => setActiveOpinions(data))
      .catch(() => setActiveOpinions(0))
      .finally(() => setLoadingActiveOpinions(false));

    fetchMostFollowedUsers()
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.data;
        setMostFollowedUsers(Array.isArray(list) ? list : []);
      })
      .catch(() => setMostFollowedUsers([]))
      .finally(() => setLoadingMostFollowed(false));

    fetchTotalAuthorizationRequests()
      .then((data) =>
        setAuthorisationRequestsAlert({ type: "warning", value: data })
      )
      .catch(() =>
        setAuthorisationRequestsAlert({ type: "warning", value: 0 })
      );

    fetchMonetizedAccounts()
      .then((items) => setMonetizedAccounts(items))
      .finally(() => setFetchingMonetizedAccounts(false));
  }, []);


  return (
    <>
      <div className="container-fluid">
        <AppBreadcrumb
          title={t("Tableau de bord")}
          rowClassName="align-items-md-center"
          titleColClassName="col-12 col-md mb-2 mb-md-0 pr-md-2"
        >
          <div className="col-12 col-md-auto d-flex justify-content-end align-items-center mb-2 mb-md-0 pl-md-0 text-right">
            <button
              type="button"
              className="btn btn-sm btn-outline-success"
              onClick={handleToggleStatsMode}
            >
              {isStatsMode
                ? i18n.t("Désactiver le mode stats")
                : i18n.t("Activer le mode stats")}
            </button>
          </div>
        </AppBreadcrumb>

            {process.env.REACT_APP_SOCIAL_HUB &&
              (authUser?.isRoot || authUser.isAdmin) && (
                <div className="row clearfix">
                  <div className="col-md-12">
                    {authorisationRequestsAlert?.value > 0 && (
                      <AppAlert
                        onClose={onClose}
                        type={authorisationRequestsAlert?.type}
                      >
                        <Link
                          className={`text-${authorisationRequestsAlert?.type}`}
                          to={`/authorization-requests`}
                        >{`${authorisationRequestsAlert?.value} demande(s) d'autorisation en attente`}</Link>
                      </AppAlert>
                    )}
                  </div>
                </div>
              )}

            <div className="row clearfix dashboard-stats-row">
              <div className="col-md-4">
                <div className="card dashboard-stats-card">
                  <div className="header font-weight-bold">
                    <h2>
                      <i className="fa fa-user-plus mr-2 stats-card-icon" aria-hidden="true" />{t("Nouveaux utilisateurs {{app}}", { app: process.env.REACT_APP_APP_NAME })}
                    </h2>
                  </div>
                  {loadingNewUsers ? (
                    <DashboardSectionLoader label={t("Chargement des nouveaux utilisateurs…")} />
                  ) : !newUsers ? (
                    <div className="body text-center text-muted dashboard-stats-card__body dashboard-stats-card__body--centered">
                      {t("Impossible de charger ces statistiques.")}
                    </div>
                  ) : (
                    <div className="body dashboard-stats-card__body">
                      <table className="table table-sm card-table mb-0">
                        <tbody>
                          <tr className="font-weight-bold">
                            <td>{t("Aujourd'hui")}</td>
                            <td className="text-right">
                              {formatCash(
                                newUsers[PERIODICITY_DAILY]["value"] *
                                  statsMultiplier
                              )}{" "}
                              <span
                                className={getVariationClassName(
                                  newUsers[PERIODICITY_DAILY]["variation"]
                                )}
                              >
                                ({newUsers[PERIODICITY_DAILY]["variation"]}%)
                                {getVariationCaret(
                                  newUsers[PERIODICITY_DAILY]["variation"]
                                )}
                              </span>
                            </td>
                          </tr>
                          <tr className="font-weight-bold">
                            <td>{t("Cette semaine")}</td>
                            <td className="text-right">
                              {formatCash(
                                newUsers[PERIODICITY_WEEKLY]["value"] *
                                  statsMultiplier
                              )}{" "}
                              <span
                                className={getVariationClassName(
                                  newUsers[PERIODICITY_WEEKLY]["variation"]
                                )}
                              >
                                ({newUsers[PERIODICITY_WEEKLY]["variation"]}%)
                                {getVariationCaret(
                                  newUsers[PERIODICITY_WEEKLY]["variation"]
                                )}
                              </span>
                            </td>
                          </tr>
                          <tr className="font-weight-bold">
                            <td>{t("Ce mois")}</td>
                            <td className="text-right">
                              {formatCash(
                                newUsers[PERIODICITY_MONTHLY]["value"] *
                                  statsMultiplier
                              )}{" "}
                              <span
                                className={getVariationClassName(
                                  newUsers[PERIODICITY_MONTHLY]["variation"]
                                )}
                              >
                                ({newUsers[PERIODICITY_MONTHLY]["variation"]}%)
                                {getVariationCaret(
                                  newUsers[PERIODICITY_MONTHLY]["variation"]
                                )}
                              </span>
                            </td>
                          </tr>
                          <tr className="font-weight-bold">
                            <td>{t("Cette année")}</td>
                            <td className="text-right">
                              {formatCash(
                                newUsers[PERIODICITY_YEARLY]["value"] *
                                  statsMultiplier
                              )}{" "}
                              <span
                                className={getVariationClassName(
                                  newUsers[PERIODICITY_YEARLY]["variation"]
                                )}
                              >
                                ({newUsers[PERIODICITY_YEARLY]["variation"]}%)
                                {getVariationCaret(
                                  newUsers[PERIODICITY_YEARLY]["variation"]
                                )}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-4">
                <div className="card dashboard-stats-card">
                  <div className="header font-weight-bold">
                    <h2>
                      <i className="fa fa-bolt mr-2 stats-card-icon" aria-hidden="true" />{t("Utilisateurs {{app}} actifs", { app: process.env.REACT_APP_APP_NAME })}
                    </h2>
                  </div>
                  {loadingActiveUsers ? (
                    <DashboardSectionLoader label={t("Chargement des utilisateurs actifs…")} />
                  ) : !activeUsers ? (
                    <div className="body text-center text-muted dashboard-stats-card__body dashboard-stats-card__body--centered">
                      {t("Impossible de charger ces statistiques.")}
                    </div>
                  ) : (
                    <div className="body dashboard-stats-card__body">
                      <table className="table table-sm card-table mb-0">
                        <tbody>
                          <tr className="font-weight-bold">
                            <td>{t("Aujourd'hui")}</td>
                            <td className="text-right">
                              {formatCash(
                                activeUsers[PERIODICITY_DAILY]["value"] *
                                  statsMultiplier
                              )}{" "}
                              <span
                                className={getVariationClassName(
                                  activeUsers[PERIODICITY_DAILY]["variation"]
                                )}
                              >
                                ({activeUsers[PERIODICITY_DAILY]["variation"]}%)
                                {getVariationCaret(
                                  activeUsers[PERIODICITY_DAILY]["variation"]
                                )}
                              </span>
                            </td>
                          </tr>
                          <tr className="font-weight-bold">
                            <td>{t("Cette semaine")}</td>
                            <td className="text-right">
                              {formatCash(
                                activeUsers[PERIODICITY_WEEKLY]["value"] *
                                  statsMultiplier
                              )}{" "}
                              <span
                                className={getVariationClassName(
                                  activeUsers[PERIODICITY_WEEKLY]["variation"]
                                )}
                              >
                                ({activeUsers[PERIODICITY_WEEKLY]["variation"]}
                                %)
                                {getVariationCaret(
                                  activeUsers[PERIODICITY_WEEKLY]["variation"]
                                )}
                              </span>
                            </td>
                          </tr>
                          <tr className="font-weight-bold">
                            <td>{t("Ce mois")}</td>
                            <td className="text-right">
                              {formatCash(
                                activeUsers[PERIODICITY_MONTHLY]["value"] *
                                  statsMultiplier
                              )}{" "}
                              <span
                                className={getVariationClassName(
                                  activeUsers[PERIODICITY_MONTHLY]["variation"]
                                )}
                              >
                                (
                                {activeUsers[PERIODICITY_MONTHLY]["variation"]}
                                %)
                                {getVariationCaret(
                                  activeUsers[PERIODICITY_MONTHLY]["variation"]
                                )}
                              </span>
                            </td>
                          </tr>
                          <tr className="font-weight-bold">
                            <td>{t("Cette année")}</td>
                            <td className="text-right">
                              {formatCash(
                                activeUsers[PERIODICITY_YEARLY]["value"] *
                                  statsMultiplier
                              )}{" "}
                              <span
                                className={getVariationClassName(
                                  activeUsers[PERIODICITY_YEARLY]["variation"]
                                )}
                              >
                                ({activeUsers[PERIODICITY_YEARLY]["variation"]}
                                %)
                                {getVariationCaret(
                                  activeUsers[PERIODICITY_YEARLY]["variation"]
                                )}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-4">
                <div className="card dashboard-stats-card">
                  <div className="header">
                    <h2><i className="fa fa-tachometer mr-2 stats-card-icon" aria-hidden="true" />{t("Autres indicateurs")}</h2>
                  </div>
                  <div className="body dashboard-stats-card__body dashboard-stats-card__body--metrics">
                    <MetricTile
                      icon="fa-users"
                      tone="blue"
                      label={t("Utilisateurs")}
                      loading={loadingTotalUsers}
                      value={formatCash(totalUsers * statsMultiplier)}
                      title={loadingTotalUsers ? "" : numberWithSeparator(totalUsers)}
                    />
                    <MetricTile
                      icon="fa-comments"
                      tone="green"
                      label={process.env.REACT_APP_SOCIAL_HUB ? t("Posts actifs") : t("Opinions actives")}
                      loading={loadingActiveOpinions}
                      value={formatCash(activeOpinions * statsMultiplier)}
                      title={loadingActiveOpinions ? "" : numberWithSeparator(activeOpinions)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <DashboardCharts
              newUsers={newUsers}
              activeUsers={activeUsers}
              loadingUsers={loadingNewUsers || loadingActiveUsers}
              monetizedAccounts={monetizedAccounts}
              loadingMonetized={fetchingMonetizedAccounts}
              multiplier={statsMultiplier}
            />

            <div className="row clearfix">
              <div className="col-md-12">
                <div className="card">
                  <div className="header font-weight-bold">
                    <h2>
                      {t("Top 10 des utilisateurs {{app}} les plus suivis", { app: process.env.REACT_APP_APP_NAME })}
                    </h2>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover table-custom spacing8">
                      <thead>
                        <tr>
                          <th>{t("Photo")}</th>
                          <th>{t("Utilisateur")}</th>
                          <th>{t("Pays")}</th>
                          <th className="text-center">{t("Followers")}</th>
                          <th className="text-center">
                            {process.env.REACT_APP_SOCIAL_HUB
                              ? i18n.t("Contacts & Abonnés")
                              : i18n.t("Potes & Fans")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingMostFollowed ? (
                          <ShimmerTableRows rows={5} columns={5} />
                        ) : mostFollowedUsers.length > 0 ? (
                          mostFollowedUsers.map((obj) => (
                            <tr
                              key={obj?.user_id}
                              onClick={() => navigateToUserProfile(obj)}
                              style={{ cursor: "pointer" }}
                            >
                              <td className="w60">
                                <img
                                  src={obj?.prof_picture || MyOpinionLogo}
                                  data-toggle="tooltip"
                                  data-placement="top"
                                  alt={`@(${obj?.user_username})`}
                                  className="w35 rounded"
                                />
                              </td>
                              <td>
                                {getUserFullName(obj, true)}{" "}
                                {obj.user_verified && (
                                  <i
                                    style={{ cursor: "pointer" }}
                                    title={t("Verified")}
                                    className="fa fa-check-circle-o text-primary mr-1"
                                  />
                                )}
                                {obj.user_gold && (
                                  <i
                                    style={{ cursor: "pointer" }}
                                    title={t("Premium / Gold")}
                                    className="fa fa-star text-warning mr-1"
                                  />
                                )}
                              </td>
                              <td>
                                {obj?.ip_country_flag &&
                                  obj?.ip_country_name && (
                                    <>
                                      <img
                                        src={obj?.ip_country_flag}
                                        data-toggle="tooltip"
                                        data-placement="top"
                                        alt={obj?.ip_country_name}
                                        className="w20 rounded"
                                      />{" "}
                                    </>
                                  )}
                                {obj?.ip_country_name || "-"}
                              </td>
                              <td
                                className={`text-center ${
                                  (obj?.all_followers_count || 0) > 1000
                                    ? "text-success"
                                    : "text-danger"
                                }`}
                              >
                                {formatCash(obj?.all_followers_count || 0)}
                              </td>
                              <td
                                className="text-center"
                                title={numberWithSeparator(
                                  obj?.total_followers || 0
                                )}
                              >
                                {formatCash(obj?.total_followers || 0)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center text-muted">
                              {t("Aucun utilisateur à afficher.")}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div className="row clearfix">
              <div className="col-md-12">
                <div className="card">
                  <div className="header font-weight-bold">
                    <h2>{t("Top des comptes monétisés")}</h2>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover table-custom spacing8">
                      <thead>
                        <tr>
                          <th>{t("Photo")}</th>
                          <th>{t("Utilisateur")}</th>
                          <th className="text-center">{t("Followers")}</th>
                          <th className="text-center">{t("Coins (USD)")}</th>
                          <th className="text-center">{t("Likes (USD)")}</th>
                          <th className="text-center">{t("Vues (USD)")}</th>
                          <th className="text-center">{t("Total (USD)")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fetchingMonetizedAccounts ? (
                          <ShimmerTableRows rows={5} columns={7} />
                        ) : monetizedAccounts &&
                          monetizedAccounts.length > 0 ? (
                          monetizedAccounts.map((row, index) => {
                            const user = row.user || {};
                            const balances = row.balances || {};
                            const coins = balances.coins || {};
                            const likes = balances.likes || {};
                            const views = balances.views || {};

                            return (
                              <tr
                                key={user.user_id || index}
                                onClick={() => navigateToUserProfile(user)}
                                style={{ cursor: "pointer" }}
                              >
                                <td className="w60">
                                  <img
                                    src={user.prof_picture || MyOpinionLogo}
                                    alt={`@(${user.user_username})`}
                                    className="w35 rounded"
                                    style={{ objectFit: "cover" }}
                                  />
                                </td>
                                <td>
                                  {getUserFullName(user, true)}{" "}
                                  {user.user_verified && (
                                    <i
                                      style={{ cursor: "pointer" }}
                                      title={t("Verified")}
                                      className="fa fa-check-circle-o text-primary mr-1"
                                    />
                                  )}
                                  {user.user_gold && (
                                    <i
                                      style={{ cursor: "pointer" }}
                                      title={t("Premium / Gold")}
                                      className="fa fa-star text-warning mr-1"
                                    />
                                  )}
                                </td>
                                <td
                                  className={`text-center font-weight-bold ${
                                    (row.all_followers_count || 0) > 1000
                                      ? "text-success"
                                      : "text-danger"
                                  }`}
                                >
                                  {formatCash(row.all_followers_count || 0)}
                                </td>
                                <td className="text-center">
                                  {formatCash(coins.usd || 0)} USD (
                                  {formatCash(coins.amount || 0)})
                                </td>
                                <td className="text-center">
                                  {formatCash(likes.usd || 0)} USD (
                                  {formatCash(likes.amount || 0)})
                                </td>
                                <td className="text-center">
                                  {formatCash(views.usd || 0)} USD (
                                  {formatCash(views.amount || 0)})
                                </td>
                                <td className="text-center text-success font-weight-bold">
                                  {formatCash(balances.total_usd || 0)} USD
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center">
                              {t("Aucun compte monétisé trouvé.")}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  authUser: state.auth.user,
});

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
